import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import analyticsAPI from 'dashboard/api/pipeline/analytics';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function useDashboardState() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();

  // State
  const selectedPipelineId = ref('all');
  const dateRange = ref('30d');
  const loading = ref(true);
  const error = ref(null);

  // Analytics data (for "all" mode)
  const analyticsData = ref(null);

  // Single pipeline data
  const singlePipelineMetrics = ref({
    totalItems: 0,
    activeItems: 0,
    wonItems: 0,
    lostItems: 0,
    itemsByStage: {},
    avgTimePerStage: {},
    slaExceededTotal: 0,
    slaExceededByStage: {},
  });
  const recentActivity = ref([]);

  // Computed
  const pipelines = computed(
    () => store.getters['pipelines/getPipelines'] || []
  );

  const isAllPipelinesMode = computed(() => selectedPipelineId.value === 'all');

  const selectedPipeline = computed(() => {
    if (isAllPipelinesMode.value) return null;
    return pipelines.value.find(
      p => String(p.id) === String(selectedPipelineId.value)
    );
  });

  const currentUser = computed(() => store.getters.getCurrentUser);

  // Date range options
  const dateRangeOptions = computed(() => [
    { value: '7d', label: t('PIPELINE.DASHBOARD.DATE_RANGE.LAST_7_DAYS') },
    { value: '30d', label: t('PIPELINE.DASHBOARD.DATE_RANGE.LAST_30_DAYS') },
    { value: '90d', label: t('PIPELINE.DASHBOARD.DATE_RANGE.LAST_90_DAYS') },
    { value: 'all', label: t('PIPELINE.DASHBOARD.DATE_RANGE.ALL_TIME') },
  ]);

  const selectedDateLabel = computed(() => {
    const option = dateRangeOptions.value.find(
      opt => opt.value === dateRange.value
    );
    return option?.label || t('PIPELINE.DASHBOARD.DATE_RANGE.LAST_30_DAYS');
  });

  // Calculate actual date range based on selection
  const computedDateRange = computed(() => {
    const now = new Date();
    const end = endOfDay(now);

    switch (dateRange.value) {
      case '7d':
        return {
          startDate: format(startOfDay(subDays(now, 7)), 'yyyy-MM-dd'),
          endDate: format(end, 'yyyy-MM-dd'),
        };
      case '30d':
        return {
          startDate: format(startOfDay(subDays(now, 30)), 'yyyy-MM-dd'),
          endDate: format(end, 'yyyy-MM-dd'),
        };
      case '90d':
        return {
          startDate: format(startOfDay(subDays(now, 90)), 'yyyy-MM-dd'),
          endDate: format(end, 'yyyy-MM-dd'),
        };
      default:
        return { startDate: null, endDate: null };
    }
  });

  // Pipeline options including "All Pipelines"
  const pipelineOptions = computed(() => {
    const allOption = {
      id: 'all',
      name: t('PIPELINE.UNIFIED_DASHBOARD.ALL_PIPELINES'),
    };
    return [allOption, ...pipelines.value];
  });

  // Sync URL with state
  const syncUrlWithState = () => {
    const query = {};
    if (selectedPipelineId.value !== 'all') {
      query.pipeline = selectedPipelineId.value;
    } else {
      query.pipeline = 'all';
    }
    if (dateRange.value !== '30d') {
      query.period = dateRange.value;
    }

    const currentQuery = { ...route.query };
    const newQuery = { ...currentQuery, ...query };

    // Only update if different
    if (
      currentQuery.pipeline !== newQuery.pipeline ||
      currentQuery.period !== newQuery.period
    ) {
      router.replace({ query: newQuery });
    }
  };

  // Initialize state from URL
  const initFromUrl = () => {
    const { pipeline, period } = route.query;

    if (pipeline) {
      selectedPipelineId.value = pipeline;
    }

    if (period && ['7d', '30d', '90d', 'all'].includes(period)) {
      dateRange.value = period;
    }
  };

  // Fetch analytics data (for "all" mode)
  const fetchAnalyticsData = async () => {
    loading.value = true;
    error.value = null;

    try {
      const params = {};
      if (computedDateRange.value.startDate) {
        params.startDate = computedDateRange.value.startDate;
        params.endDate = computedDateRange.value.endDate;
      }

      const response = await analyticsAPI.getDashboard(params);
      analyticsData.value = response.data;
    } catch (err) {
      error.value = err.message || t('PIPELINE.ANALYTICS.ERROR_LOADING');
      analyticsData.value = null;
    } finally {
      loading.value = false;
    }
  };

  // Helper to get stage name
  const getStageName = item => {
    const stageId = item.current_stage?.name || item.pipeline_stage;

    if (!stageId) {
      return 'Unknown';
    }

    if (selectedPipeline.value?.stages) {
      const stageConfig = selectedPipeline.value.stages[stageId];
      if (stageConfig?.name) {
        return stageConfig.name;
      }
    }

    return stageId;
  };

  // Fetch single pipeline data
  const fetchSinglePipelineData = async () => {
    if (!selectedPipeline.value) return;

    loading.value = true;
    error.value = null;

    try {
      const response = await PipelineCardsAPI.getCards(
        selectedPipeline.value.id
      );
      let items = response.data?.data || response.data || [];

      // Filter by date range if set
      const { startDate: rangeStart, endDate: rangeEnd } =
        computedDateRange.value;
      if (rangeStart && rangeEnd) {
        const startMs = new Date(rangeStart).getTime();
        const endMs = new Date(rangeEnd).getTime() + 86400000; // end of day
        items = items.filter(item => {
          const created = new Date(item.created_at).getTime();
          return created >= startMs && created <= endMs;
        });
      }

      // Calculate metrics
      const totalItems = items.length;
      const activeItems = items.filter(i => i.status === 'open').length;
      const wonItems = items.filter(i => i.status === 'won').length;
      const lostItems = items.filter(i => i.status === 'lost').length;

      // Calculate value metrics (only cards that have a registered value)
      const cardsWithValue = items.filter(
        i => parseFloat(i.item_details?.value) > 0
      );
      const totalValue = cardsWithValue.reduce(
        (sum, i) => sum + parseFloat(i.item_details.value),
        0
      );
      const cardsWithValueCount = cardsWithValue.length;
      const avgValue =
        cardsWithValueCount > 0 ? totalValue / cardsWithValueCount : 0;

      // Count items by stage
      const itemsByStage = {};
      const timeByStage = {};
      const slaExceededByStage = {};
      let slaExceededTotal = 0;

      items.forEach(item => {
        const stageName = getStageName(item);
        itemsByStage[stageName] = (itemsByStage[stageName] || 0) + 1;

        // Fase 2.4 — Leads esquecidos: SLA excedido e ainda na stage original
        const sla = item.item_details?.sla;
        if (
          sla?.exceeded_at &&
          (sla.stage === undefined || sla.stage === item.pipeline_stage) &&
          item.status === 'open'
        ) {
          slaExceededByStage[stageName] =
            (slaExceededByStage[stageName] || 0) + 1;
          slaExceededTotal += 1;
        }

        // P1.3 — calcula client-side a partir de stage_entered_at.
        // Backend não envia mais time_in_current_stage (valor mudava a cada
        // segundo, qualquer cache server-side era stale).
        let timeInStage = 0;
        if (item.stage_entered_at) {
          timeInStage = Math.round(
            (Date.now() - new Date(item.stage_entered_at).getTime()) / 1000
          );
        } else if (item.time_in_current_stage) {
          // Fallback para compatibilidade caso o backend ainda mande
          timeInStage = item.time_in_current_stage;
        }

        if (timeInStage && timeInStage > 0) {
          if (!timeByStage[stageName]) {
            timeByStage[stageName] = [];
          }
          timeByStage[stageName].push(timeInStage);
        }
      });

      // Calculate average time per stage (time values are in seconds)
      const avgTimePerStage = {};
      Object.entries(timeByStage).forEach(([stage, times]) => {
        const avg = times.reduce((sum, time) => sum + time, 0) / times.length;
        avgTimePerStage[stage] = Math.round(avg / (60 * 60 * 24));
      });

      singlePipelineMetrics.value = {
        totalItems,
        activeItems,
        wonItems,
        lostItems,
        totalValue,
        avgValue,
        cardsWithValueCount,
        itemsByStage,
        avgTimePerStage,
        slaExceededTotal,
        slaExceededByStage,
      };

      // Get recent activity
      recentActivity.value = items
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 10)
        .map(item => ({
          id: item.id,
          title: item.title || item.item_details?.title || 'Untitled',
          stage: getStageName(item),
          updatedAt: item.updated_at,
        }));
    } catch (err) {
      error.value = err.message || t('PIPELINE.DASHBOARD.FETCH_ERROR');
    } finally {
      loading.value = false;
    }
  };

  // Load data based on mode
  const loadData = async () => {
    if (isAllPipelinesMode.value) {
      await fetchAnalyticsData();
    } else {
      await fetchSinglePipelineData();
    }
  };

  // Select pipeline handler
  const selectPipeline = pipelineId => {
    selectedPipelineId.value = pipelineId;
    syncUrlWithState();
    loadData();
  };

  // Select date range handler
  const selectDateRange = range => {
    dateRange.value = range;
    syncUrlWithState();
    loadData();
  };

  // Retry loading
  const retryLoad = () => {
    loadData();
  };

  // Format date helper
  const formatDate = date => {
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale: ptBR });
  };

  // Check if analytics has no data
  const hasNoAnalyticsData = computed(() => {
    if (!analyticsData.value) return true;
    const summary = analyticsData.value.pipeline_summary || {};
    return (summary.total_items || 0) === 0;
  });

  // Check if single pipeline has no data
  const hasNoSinglePipelineData = computed(() => {
    return singlePipelineMetrics.value.totalItems === 0;
  });

  // Initialize
  onMounted(async () => {
    await store.dispatch('pipelines/fetch');
    initFromUrl();
    await loadData();
  });

  // Watch for URL changes (back/forward navigation)
  watch(
    () => route.query,
    newQuery => {
      const { pipeline, period } = newQuery;

      if (pipeline && pipeline !== selectedPipelineId.value) {
        selectedPipelineId.value = pipeline;
        loadData();
      }

      if (period && period !== dateRange.value) {
        dateRange.value = period;
        loadData();
      }
    }
  );

  return {
    // State
    selectedPipelineId,
    dateRange,
    loading,
    error,
    analyticsData,
    singlePipelineMetrics,
    recentActivity,

    // Computed
    pipelines,
    isAllPipelinesMode,
    selectedPipeline,
    currentUser,
    dateRangeOptions,
    selectedDateLabel,
    computedDateRange,
    pipelineOptions,
    hasNoAnalyticsData,
    hasNoSinglePipelineData,

    // Methods
    selectPipeline,
    selectDateRange,
    retryLoad,
    loadData,
    formatDate,
    getStageName,
  };
}
